<script lang="ts">
import { defineComponent as __MACROS_defineComponent } from "vue";
export default /*#__PURE__*/ __MACROS_defineComponent({
  components: {
    ComponentProxy, // for createColDefs
  },
});
</script>
<script setup lang="ts">
import { AgGridVue } from 'ag-grid-vue';
import { computed, onUnmounted, ref, shallowRef, watch, watchEffect } from 'vue';
import { storeToRefs } from 'pinia';
import { useOrderFormStore } from '@/modules/marketplace/store/order-form-store';
import { useSocketExplorer } from '@/modules/marketplace/composables/socket-explorer';
import { ComponentProxy } from '@/modules/common/components/ag-table/columns/utils';
import MarketplaceOrderForm from '@/modules/marketplace/components/MarketplaceOrderForm.vue';
import MarketplaceActionConfirmationSnackbar from '@/modules/marketplace/components/MarketplaceActionConfirmationSnackbar.vue';
import { ConfirmationSnackbarOptions } from '@/modules/marketplace/types/marketplace';
import {
  createColDefs,
  defaultColDef,
} from '@/modules/common/components/ag-table/columns/marketplace-explorer';
import SimpleSecuritySearch from '@/modules/manual-loan/components/SimpleSecuritySearch.vue';
import {
  CellMouseOutEvent,
  CellMouseOverEvent,
  GetContextMenuItemsParams,
} from 'ag-grid-enterprise';
import DashboardPanel from '@/modules/dashboard/components/DashboardPanel.vue';
import { getRowId } from '@/modules/marketplace/helpers/explorer';
import MarketplaceExecutionsTable from '@/modules/marketplace/components/MarketplaceExecutionsTable.vue';
import { createDataSource } from '@/modules/marketplace/services/datasource-explorer';
import { useDownloadExplorer } from '@/modules/marketplace/composables/download-explorer';
import { Side } from '@/connect/gen/consts/commonconsts_pb';
import WatchlistSelector from '@/modules/marketplace/components/WatchlistSelector.vue';
import MultipleCounterpartySearch from '@/modules/user-accounts/components/MultipleCounterpartySearch.vue';
import MultipleChipAgreementSelector from '@/modules/agreements/components/MultipleChipAgreementSelector.vue';
import SettlementTypeSelector from '@/modules/marketplace/components/SettlementTypeSelector.vue';
import MultipleSecurityInput from '@/modules/common/components/MultipleSecurityInput.vue';
import { rateTypeOptions } from '@/modules/marketplace/helpers/marketplace';
import { useAgreementsStore } from '@/store/agreements-store';
import BrokerCompanySelector from '@/modules/broker-admin/views/BrokerCompanySelector.vue';
import { useRateTypeStore } from '@/store/rate-type-store';
import SavedViewsManager from '@/modules/saved-views/components/SavedViewsManager.vue';
import SavedViewsTabs from '@/modules/saved-views/components/SavedViewsTabs.vue';
import {
  masterFilterItems,
  useGridApiExplorerStore,
} from '@/modules/marketplace/store/grid-api-explorer-store';
import { savedViewsRegistry } from '@/modules/saved-views/store/registry';
import { Permission, useHasPermission } from '@/composables/user-permissions';

const props = withDefaults(
  defineProps<{
    savedViewId?: string;
    asBroker?: boolean;
  }>(),
  {
    savedViewId: undefined,
    asBroker: false,
  }
);

;

const gridApiExplorerStore = useGridApiExplorerStore();
gridApiExplorerStore.setAsBroker(props.asBroker);
const savedViewsStore = savedViewsRegistry.orderbook();

if (props.savedViewId) {
  savedViewsStore.setInitialSavedViewId(props.savedViewId);
}

function refreshData() {
  gridApiExplorerStore.getGridApi().refreshServerSide({ purge: false });
}

savedViewsStore.registerRefreshData(refreshData);

const orderFormStore = useOrderFormStore();
const agreementsStore = useAgreementsStore();
const downloadExplorer = useDownloadExplorer();

const isCsvLoading = computed(() => downloadExplorer.status.value === 'csv');
const isXlsxLoading = computed(() => downloadExplorer.status.value === 'xlsx');

watch(
  () => savedViewsStore.isSavedViewsExpanded,
  // timeout is needed because SavedViewsManager has css transitions
  () => setTimeout(() => gridApiExplorerStore.resizeColumns(), 100)
);

const {
  selectedMasterFilter,
  filters,
  sideSelectorItems,
  oppositeSide,
  dataConfig,
  securities,
  canFilter,
  initialState,
} = storeToRefs(gridApiExplorerStore);

const canCreateOrder = useHasPermission(Permission.OMS_ACTIONS);

const tableKey = shallowRef('');
watchEffect(() => {
  tableKey.value = savedViewsStore.selectedView?.id ?? 'default';
});

const rateTypeStore = storeToRefs(useRateTypeStore());
const rateType = rateTypeStore.marketplaceRateType;

const isExecutionsPanelCollapsed = ref(
  sessionStorage.getItem('marketplace-executions-collapsed') === 'true'
);
watchEffect(() =>
  sessionStorage.setItem(
    'marketplace-executions-collapsed',
    isExecutionsPanelCollapsed.value.toString()
  )
);

const colDefs = computed(() =>
  createColDefs(filters.value.tradeIntention, oppositeSide.value, rateType.value, props.asBroker)
);

watchEffect(async () => {
  await agreementsStore.loadAgreements(dataConfig.value.companyIdFilter);
});

const dataSource = computed(() => createDataSource(dataConfig.value));

const socketExplorer = useSocketExplorer(dataConfig.value);

watchEffect(() => {
  socketExplorer.setDataConfig(dataConfig.value);
});

onUnmounted(() => {
  savedViewsStore.$dispose();
});

const confirmationSnackbarOptions = shallowRef<ConfirmationSnackbarOptions | null>(null);

// only change the background color of the hovered direction (and not the entire row)
function onCellMouseOver(params: CellMouseOverEvent) {
  const colId = params.column.getColId();
  const direction = colId.startsWith('borrow')
    ? 'borrow'
    : colId.startsWith('lend')
      ? 'lend'
      : null;
  if (!direction || !params.data[`${direction}Order`]) return;
  getRowFromParamsEvent(params)
    ?.querySelectorAll(`[col-id^="${direction}"]`)
    .forEach((el) => el.classList.add('hovered-direction'));
}

function onCellMouseOut(params: CellMouseOutEvent) {
  getRowFromParamsEvent(params)
    ?.querySelectorAll('.ag-cell')
    .forEach((el) => el.classList.remove('hovered-direction'));
}

function getRowFromParamsEvent(params: CellMouseOutEvent | CellMouseOverEvent) {
  return (params.event?.target as HTMLElement)?.closest('.ag-row');
}

function getContextMenuItems(params: GetContextMenuItemsParams) {
  // all the defaults except "export"
  return params.defaultItems?.filter((item) => item !== 'export');
}
</script>

<template>
  <div class="root orderbook">
    <SavedViewsTabs v-if="!asBroker" class="ml-n2" name="orderbook" />
    <div class="d-flex align-center justify-space-between">
      <div class="d-flex gap-1">
        <BrokerCompanySelector
          v-if="props.asBroker"
          v-model="filters.selectedCompany"
          autofocus
          class="company-selector"
          data-test="marketplace-broker-company-selector"
        />
        <template v-if="canFilter">
          <VSelect
            v-model="filters.tradeIntention"
            class="side-selector ml-2"
            data-test="marketplace-side-selector"
            :items="sideSelectorItems"
          />
          <VSelect
            v-model="selectedMasterFilter"
            class="master-filter-selector"
            clearable
            data-test="marketplace-master-filter-search"
            :items="masterFilterItems"
            label="Filter by"
            :menu-props="{ maxHeight: 500 }"
            @change="gridApiExplorerStore.emptyFilters"
          />
          <SimpleSecuritySearch
            v-if="selectedMasterFilter === 'security'"
            v-model="filters.security"
            class="simple-security-search"
            clearable
            data-test="marketplace-security-search"
            label="Security"
          />
          <MultipleSecurityInput
            v-if="selectedMasterFilter === 'securities'"
            v-model="filters.securities"
            data-test="marketplace-securities-input"
          />
          <WatchlistSelector
            v-if="selectedMasterFilter === 'watchlist'"
            v-model="filters.watchlist"
            data-test="marketplace-watchlist-selector"
          />
          <MultipleCounterpartySearch
            v-if="selectedMasterFilter === 'company' || selectedMasterFilter === 'activeAndCompany'"
            :counterparties.sync="filters.companies"
            data-test="marketplace-counterparty-search"
            label="Select companies"
            mode="chip"
          />
          <MultipleChipAgreementSelector
            v-if="
              selectedMasterFilter === 'agreement' || selectedMasterFilter === 'activeAndAgreement'
            "
            :agreements.sync="filters.agreements"
            :company-id-filter="filters.selectedCompany?.companyId"
            data-test="marketplace-agreement-search"
            label="Select agreements"
          />
          <SettlementTypeSelector
            v-if="
              selectedMasterFilter === 'settlement' ||
              selectedMasterFilter === 'activeAndSettlement'
            "
            v-model="filters.settlementType"
            data-test="marketplace-settlement-type-selector"
          />
          <VTooltip bottom open-delay="300">
            <template #activator="{ on, attrs }">
              <VBtn
                v-bind="attrs"
                class="icon-opacity mt-5 mr-2 ml-n2"
                data-test="marketplace-reset-filters-button"
                icon
                small
                v-on="on"
                @click="savedViewsStore.selectView(null)"
              >
                <VIcon>mdi-filter-remove-outline</VIcon>
              </VBtn>
            </template>
            <span>Reset Filters</span>
          </VTooltip>
        </template>
      </div>
      <template v-if="canFilter">
        <VSelect
          v-model="rateType"
          class="ml-auto mr-4 flex-grow-0"
          :items="rateTypeOptions"
          label="Rate Type"
        />
        <div class="action-icons icon-opacity">
          <VTooltip bottom open-delay="300">
            <template #activator="{ on, attrs }">
              <VBtn
                v-bind="attrs"
                class="mt-n2"
                data-test-id="marketplace-download-csv"
                :disabled="isCsvLoading || isXlsxLoading"
                :loading="isCsvLoading"
                small
                v-on="on"
                @click="downloadExplorer.download(dataConfig, 'csv')"
              >
                CSV
                <VIcon small> mdi-download </VIcon>
              </VBtn>
            </template>
            <span>Download CSV</span>
          </VTooltip>
          <VTooltip bottom open-delay="300">
            <template #activator="{ on, attrs }">
              <VBtn
                v-bind="attrs"
                class="mt-n2"
                data-test-id="marketplace-download-xlsx"
                :disabled="isXlsxLoading || isCsvLoading"
                :loading="isXlsxLoading"
                small
                v-on="on"
                @click="downloadExplorer.download(dataConfig, 'xlsx')"
              >
                XLSX
                <VIcon small> mdi-download </VIcon>
              </VBtn>
            </template>
            <span>Download XLSX</span>
          </VTooltip>
          <VTooltip bottom open-delay="300">
            <template #activator="{ on, attrs }">
              <VBtn
                v-bind="attrs"
                class="mt-n2"
                data-test="marketplace-collapse-groups-button"
                icon
                small
                v-on="on"
                @click="gridApiExplorerStore.collapseGroups"
              >
                <VIcon>mdi-arrow-collapse-vertical</VIcon>
              </VBtn>
            </template>
            <span>Collapse Groups</span>
          </VTooltip>
          <VTooltip bottom open-delay="300">
            <template #activator="{ on, attrs }">
              <VBtn
                v-bind="attrs"
                class="mt-n2"
                data-test="marketplace-expand-groups-button"
                icon
                small
                v-on="on"
                @click="gridApiExplorerStore.expandGroups"
              >
                <VIcon>mdi-arrow-expand-vertical</VIcon>
              </VBtn>
            </template>
            <span>Expand Groups</span>
          </VTooltip>
          <VTooltip v-if="!props.asBroker && canCreateOrder" bottom open-delay="300">
            <template #activator="{ on, attrs }">
              <VBtn
                v-bind="attrs"
                class="mt-n2"
                data-test="marketplace-order-form-button"
                icon
                small
                v-on="on"
                @click="orderFormStore.resetAndShowForm"
              >
                <VIcon>mdi-clipboard-text-outline </VIcon>
              </VBtn>
            </template>
            <span>Display Form</span>
          </VTooltip>
          <VTooltip v-if="!asBroker" bottom open-delay="300">
            <template #activator="{ on, attrs }">
              <VBtn
                v-bind="attrs"
                class="mt-n2"
                data-test="marketplace-saved-views-button"
                icon
                small
                v-on="on"
                @click="savedViewsStore.toggleSavedViewsExpanded"
              >
                <VIcon>mdi-view-list</VIcon>
              </VBtn>
            </template>
            <span>{{
              savedViewsStore.isSavedViewsExpanded ? 'Collapse Saved Views' : 'Expand Saved Views'
            }}</span>
          </VTooltip>
        </div>
      </template>
    </div>

    <div class="wrapper">
      <div class="tables-container">
        <div class="saved-views-layout">
          <div class="content-wrapper">
            <div class="main-content">
              <div class="d-flex" style="height: 100%">
                <div style="flex-grow: 1; height: 100%">
                  <AgGridVue
                    :key="tableKey"
                    :cache-block-size="20000"
                    class="ag-theme-balham-dark pl-1 pr-1"
                    :column-defs="colDefs"
                    data-test="marketplace-explorer-grid"
                    :default-col-def="defaultColDef"
                    :get-context-menu-items="getContextMenuItems"
                    :get-row-id="getRowId"
                    group-display-type="groupRows"
                    :initial-state="initialState"
                    :is-server-side-group-open-by-default="
                      gridApiExplorerStore.isServerSideGroupOpenByDefault
                    "
                    :max-blocks-in-cache="2"
                    row-model-type="serverSide"
                    :server-side-datasource="dataSource"
                    suppress-movable-columns
                    @cell-mouse-out="onCellMouseOut"
                    @cell-mouse-over="onCellMouseOver"
                    @column-visible="gridApiExplorerStore.resizeColumns"
                    @grid-ready="gridApiExplorerStore.onGridReady"
                  />
                </div>
                <MarketplaceOrderForm
                  v-if="!props.asBroker"
                  v-model="orderFormStore.showForm"
                  class="form"
                  :new-order-side="filters.tradeIntention === Side.BORROW ? 'BORROW' : 'LOAN'"
                  :order-ref="orderFormStore.orderRef"
                  :order-to-match="orderFormStore.orderToMatch"
                  :should-split="orderFormStore.shouldSplit"
                  show-reset-button
                  :show-summary.sync="orderFormStore.showSummary"
                  @action="
                    confirmationSnackbarOptions = { ...$event, showViewButton: false };
                    orderFormStore.orderRef = null;
                    orderFormStore.orderToMatch = null;
                    orderFormStore.shouldSplit = false;
                  "
                />
              </div>
            </div>
            <div :class="savedViewsStore.isSavedViewsExpanded ? 'ml-4' : ''">
              <SavedViewsManager v-if="!asBroker" name="orderbook" />
            </div>
          </div>
        </div>

        <DashboardPanel
          v-if="!props.asBroker"
          :is-collapsed="isExecutionsPanelCollapsed"
          title="executions"
          @update:is-collapsed="isExecutionsPanelCollapsed = $event"
        >
          <!-- v-if avoids data re-fetching every 1s when collapsed -->
          <MarketplaceExecutionsTable
            v-if="!isExecutionsPanelCollapsed"
            :filtered-by-cusips="securities?.map((security) => security.cusip)"
          />
        </DashboardPanel>
      </div>
    </div>
    <MarketplaceActionConfirmationSnackbar
      v-if="!props.asBroker && confirmationSnackbarOptions"
      :options="confirmationSnackbarOptions"
      @close="confirmationSnackbarOptions = null"
    />
  </div>
</template>

<style lang="scss" scoped>
.root {
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
}

.wrapper {
  display: flex;
  flex: 1;
  flex-direction: row;
  overflow: hidden;
  height: 100%;
}

.gap-1 {
  gap: 1rem;
}

.company-selector {
  width: 325px;
}

.action-icons {
  display: flex;
  gap: 0.25rem;
}

.icon-opacity {
  opacity: 0.8;
}

.side-selector {
  max-width: 6rem;
}

.simple-security-search {
  max-width: 10rem;
  margin-left: 0.5rem;
}

.master-filter-selector {
  max-width: 25rem;
}

.form-hidden {
  display: none;
}

.ag-theme-balham-dark {
  height: 100%;
  width: 100%;
}

::v-deep {
  .ag-theme-balham-dark {
    --ag-header-cell-hover-background-color: transparent;
    --ag-range-selection-border-color: transparent;
  }

  .ag-row {
    --ag-row-hover-color: transparent;
  }

  .highlighted-border-right {
    border-right: 1px solid rgb(92, 92, 92);
  }

  .ag-row-even:not(.ag-row-level-0) {
    background-color: #262c2e;
  }

  .ag-row-group {
    background-color: #404b4e;
  }

  .borrow-header {
    background-color: rgb(40, 90, 42) !important;
    color: white;
  }

  .lend-header {
    background-color: hsl(207, 44%, 49%) !important;
    color: white;
  }

  .ioi-order {
    font-style: italic;
    color: #999;
  }

  .ag-header-cell {
    &.header-center .ag-header-cell-label {
      justify-content: flex-center;
    }

    &.header-right .ag-header-cell-label {
      justify-content: flex-end;
    }
  }

  .hovered-direction {
    background-color: #404b4eb5;
  }

  .ag-row .ag-cell-data-changed {
    background-color: #ddd !important;
    color: black !important;
  }
}

.tables-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 1rem;

  > * {
    &:first-child {
      flex: 2;
    }
    &:last-child {
      flex: 1;
    }
  }
}

.saved-views-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

.content-wrapper {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.main-content {
  flex: 1;
  overflow: hidden;
}
</style>
